import { useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import type { ISpProfilePublic } from '@brushfire/types'
import accIcon from '@/features/account/assets/acc-icon.svg'
import { useNotify } from '@/shared/toast/ToastProvider'
import {
  accountAvatarEmptyEditSx,
  accountAvatarImageEditSx,
  accountAvatarUploadActionZoneSx,
  accountAvatarUploadActionsOverlaySx,
  accountAvatarUploadAvatarSx,
  accountAvatarUploadCancelButtonSx,
  accountAvatarUploadConfirmButtonSx,
  accountAvatarUploadRootSx,
  accountEditCameraButtonSx,
} from '@/features/account/lib/account-settings-tokens'
import { RiCameraLine } from '@/shared/icons/remix'

const MAX_BYTES = 5 * 1024 * 1024
const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png'])

interface Props {
  profile: ISpProfilePublic
  onUpload: (file: File) => Promise<void>
  isUploading: boolean
}

export function ProfilePictureUpload({ profile, onUpload, isUploading }: Readonly<Props>) {
  const notify = useNotify()
  const inputRef = useRef<HTMLInputElement>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const displayUrl = previewUrl ?? profile.avatarUrl ?? null
  const hasDisplayImage = Boolean(displayUrl)
  const avatarFrameSx = hasDisplayImage ? accountAvatarImageEditSx : accountAvatarEmptyEditSx

  const resetSelection = () => {
    setPreviewUrl(null)
    setSelectedFile(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleFileChange = (file: File | undefined) => {
    if (!file) return
    if (!ALLOWED_TYPES.has(file.type)) {
      if (inputRef.current) inputRef.current.value = ''
      notify.showToast('Invalid file type. Please upload a .jpg or .png image.', 'error')
      return
    }
    if (file.size > MAX_BYTES) {
      if (inputRef.current) inputRef.current.value = ''
      notify.showToast('File size must be less than 5MB. Please choose a smaller image.', 'error')
      return
    }
    setSelectedFile(file)
    setPreviewUrl(URL.createObjectURL(file))
  }

  const handleUpload = async () => {
    if (!selectedFile) return
    try {
      await onUpload(selectedFile)
      resetSelection()
    } catch {
      notify.showToast('Unable to upload image. Please try again.', 'error')
    }
  }

  return (
    <Box sx={accountAvatarUploadRootSx}>
      <Box sx={accountAvatarUploadAvatarSx}>
        <Box sx={avatarFrameSx} data-testid="account-profile-avatar">
          {hasDisplayImage ? (
            <Box
              component="img"
              src={displayUrl ?? undefined}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Box
              component="img"
              src={accIcon}
              alt=""
              aria-hidden
              sx={{ width: 68, height: 68 }}
            />
          )}
        </Box>
        {selectedFile ? null : (
          <IconButton
            aria-label="Choose profile picture"
            onClick={() => inputRef.current?.click()}
            disabled={isUploading}
            data-testid="account-avatar-camera"
            sx={accountEditCameraButtonSx}
          >
            <RiCameraLine size={26} />
          </IconButton>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          hidden
          data-testid="account-avatar-file-input"
          onChange={(e) => handleFileChange(e.target.files?.[0])}
        />
      </Box>

      <Box sx={accountAvatarUploadActionZoneSx}>
        {selectedFile ? (
          <Box sx={accountAvatarUploadActionsOverlaySx}>
            <Button
              sx={accountAvatarUploadCancelButtonSx}
              onClick={resetSelection}
              disabled={isUploading}
              data-testid="account-avatar-upload-cancel"
            >
              Cancel
            </Button>
            <Button
              sx={accountAvatarUploadConfirmButtonSx}
              onClick={() => void handleUpload()}
              disabled={isUploading}
              data-testid="account-avatar-upload-confirm"
            >
              {isUploading ? <CircularProgress size={16} color="inherit" /> : 'Upload'}
            </Button>
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
